// Shared visual primitives for both portals. Thin, class-composing wrappers
// over the existing token utilities (bg-canyon, rounded-card, …) so the
// contrast overrides in tokens.css keep matching the same class pairs.
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Link, type LinkProps } from '@tanstack/react-router';
import { InlineLoader } from './Loader';
import { buttonClass, type ButtonSize, type ButtonVariant } from './classes';

// ---- Buttons --------------------------------------------------------------

export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  icon,
  className = '',
  children,
  disabled,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  icon?: ReactNode;
}) {
  return (
    <button type="button" {...rest} disabled={disabled || loading} className={buttonClass(variant, size, className)}>
      {loading ? <InlineLoader /> : icon}
      {children}
    </button>
  );
}

/** A router Link dressed as a button. */
export function ButtonLink({
  variant = 'secondary',
  size = 'md',
  icon,
  className = '',
  children,
  ...rest
}: Omit<LinkProps, 'children'> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: ReactNode;
  className?: string;
  children?: ReactNode;
}) {
  return (
    <Link {...rest} className={buttonClass(variant, size, className)}>
      {icon}
      {children}
    </Link>
  );
}

// ---- Surfaces -------------------------------------------------------------

export function Card({
  children,
  className = '',
  padding = 'lg',
  as: Tag = 'div',
  ...rest
}: {
  children: ReactNode;
  className?: string;
  padding?: 'none' | 'md' | 'lg' | 'xl';
  as?: 'div' | 'section' | 'article' | 'li';
} & Record<string, unknown>) {
  const pad = padding === 'none' ? '' : padding === 'md' ? 'p-md' : padding === 'xl' ? 'p-xl' : 'p-lg';
  return (
    <Tag {...rest} className={`rounded-card border border-border bg-surface shadow-sm ${pad} ${className}`}>
      {children}
    </Tag>
  );
}

/** Page title row: title + optional lede on the left, actions on the right. */
export function PageHeader({
  title,
  lede,
  actions,
  eyebrow,
}: {
  title: ReactNode;
  lede?: ReactNode;
  actions?: ReactNode;
  eyebrow?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-md">
      <div className="min-w-0">
        {eyebrow ? <div className="mb-xs text-small text-ink-2">{eyebrow}</div> : null}
        <h1 className="text-screen-title font-extrabold tracking-tight text-ink">{title}</h1>
        {lede ? <p className="mt-xs max-w-[64ch] text-body text-ink-2">{lede}</p> : null}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-sm">{actions}</div> : null}
    </div>
  );
}

export function SectionTitle({ children, aside }: { children: ReactNode; aside?: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-md">
      <h2 className="text-h3 font-bold text-ink">{children}</h2>
      {aside}
    </div>
  );
}

/** Dashboard number. `tone` colours the value: `attention` for anything the
 * reader should act on (money owed, a queue), default ink otherwise. */
export function StatTile({
  label,
  value,
  hint,
  tone = 'default',
  icon,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  tone?: 'default' | 'attention' | 'good';
  icon?: ReactNode;
}) {
  const valueClass = tone === 'attention' ? 'text-canyon' : tone === 'good' ? 'text-success' : 'text-ink';
  return (
    <Card padding="md" className="relative overflow-hidden">
      <div className="flex items-start justify-between gap-sm">
        <p className="text-small text-ink-2">{label}</p>
        {icon ? <span className="grid h-7 w-7 place-items-center rounded-tile bg-tint-sand text-canyon [&>svg]:h-4 [&>svg]:w-4">{icon}</span> : null}
      </div>
      <p className={`mt-xs text-display font-extrabold leading-none tracking-tight ${valueClass}`}>{value}</p>
      {hint ? <p className="mt-sm text-caption text-ink-3">{hint}</p> : null}
    </Card>
  );
}

/** Empty states are an invitation to act: say what belongs here and offer
 * the one next step when there is one. */
export function EmptyState({
  title,
  body,
  action,
  icon,
  testId,
}: {
  title: ReactNode;
  body?: ReactNode;
  action?: ReactNode;
  icon?: ReactNode;
  testId?: string;
}) {
  return (
    <div
      data-testid={testId}
      className="flex flex-col items-center gap-sm rounded-card border border-dashed border-border bg-surface px-lg py-xxl text-center"
    >
      {icon ? <span className="grid h-10 w-10 place-items-center rounded-tile bg-tint-sand text-canyon [&>svg]:h-5 [&>svg]:w-5">{icon}</span> : null}
      <p className="text-h3 font-bold text-ink">{title}</p>
      {body ? <p className="max-w-[48ch] text-body text-ink-2">{body}</p> : null}
      {action ? <div className="mt-xs">{action}</div> : null}
    </div>
  );
}

// ---- Forms ----------------------------------------------------------------

export function Field({
  label,
  hint,
  htmlFor,
  children,
  className = '',
}: {
  label: ReactNode;
  hint?: ReactNode;
  htmlFor?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <label htmlFor={htmlFor} className={`flex flex-col gap-xs text-small font-semibold text-ink-2 ${className}`}>
      {label}
      {children}
      {hint ? <span className="text-caption font-normal text-ink-3">{hint}</span> : null}
    </label>
  );
}

/** Two-to-four option switch (list tabs, filters). */
export function Segmented<T extends string>({
  value,
  onChange,
  options,
  ariaLabel,
}: {
  value: T;
  onChange: (next: T) => void;
  options: Array<{ value: T; label: ReactNode; count?: number }>;
  ariaLabel?: string;
}) {
  return (
    <div role="group" aria-label={ariaLabel} className="inline-flex w-fit gap-xs rounded-button border border-border bg-surface p-xs">
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(o.value)}
            className={`inline-flex items-center gap-xs rounded-[9px] px-md py-xs text-small font-semibold ${
              active ? 'bg-ink-bar text-white shadow-sm' : 'text-ink-2 hover:bg-surface-2 hover:text-ink'
            }`}
          >
            {o.label}
            {typeof o.count === 'number' ? (
              <span
                className={`rounded-chip px-[6px] text-caption tabular-nums ${active ? 'bg-white/20 text-white' : 'bg-surface-2 text-ink-2'}`}
              >
                {o.count}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

/** Soft notice strip. */
export function Notice({
  tone = 'info',
  children,
  role,
  testId,
}: {
  tone?: 'info' | 'warning' | 'danger' | 'success';
  children: ReactNode;
  role?: 'status' | 'alert';
  testId?: string;
}) {
  const cls = {
    info: 'border-sky bg-tint-sky text-ink',
    warning: 'border-warning bg-tint-warning text-ink',
    danger: 'border-danger bg-tint-warning text-danger',
    success: 'border-success bg-tint-success text-success',
  }[tone];
  return (
    <p role={role} data-testid={testId} className={`rounded-input border px-md py-sm text-body ${cls}`}>
      {children}
    </p>
  );
}
